import React, { useState } from 'react';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { ComposeToolbar } from '@/components/ComposeToolbar';
import { Send } from '@/components/Icons/Send';
import { LinkModal } from '@/components/LinkModal';
import { PendingAttachmentChip } from '@/components/PendingAttachmentChip';
import type { PotentialAttachment } from '@/types/attachment';
import { ButtonVariant, Size } from '@/types/components';
import { Capitalize } from '@/utils/editor';

interface Props {
  attachments: PotentialAttachment[];
  isSending: boolean;
  canSend: boolean;
  onRemoveAttachment: (key: string) => void;
  onSend: (text: string) => Promise<boolean>;
}

export const MessageComposer: React.FC<Props> = ({
  attachments,
  isSending,
  canSend,
  onRemoveAttachment,
  onSend,
}) => {
  const { t } = useTranslation();
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false } }),
      Capitalize,
      Placeholder.configure({
        placeholder: t('mail.composeModal.bodyPlaceholder'),
      }),
    ],
  });

  const isEmpty = useEditorState({
    editor,
    selector: ({ editor: instance }) => instance?.isEmpty ?? true,
  });

  const hasContent = !isEmpty || attachments.length > 0;

  const handleSend = async () => {
    if (!editor) {
      return;
    }

    const isSent = await onSend(isEmpty ? '' : editor.getHTML());

    if (isSent) {
      editor.commands.clearContent();
    }
  };

  return (
    <div className="flex flex-col gap-4 px-4 md:px-5">
      <div
        onClick={() => editor?.chain().focus().run()}
        className="flex cursor-text flex-col gap-2 rounded-xl border border-divider-surface bg-on-primary-surface px-4 pb-4 max-md:pt-4"
      >
        <div className="max-md:hidden">
          <ComposeToolbar
            isEmbedded
            editor={editor}
            onLinkClick={() => setIsLinkModalOpen(true)}
          />
        </div>

        {!isEmpty && (
          <p className="text-caption-12 text-subtle-foreground">
            {t('diary.homeTask.messageLabel')}
          </p>
        )}

        <EditorContent
          editor={editor}
          className="compose-editor large-placeholder rich-text text-body-14 min-h-10 text-foreground md:min-h-6"
        />

        {attachments.length > 0 && (
          <div
            onClick={(event) => event.stopPropagation()}
            className="flex flex-col items-start gap-2 md:flex-row md:flex-wrap"
          >
            {attachments.map((attachment) => (
              <PendingAttachmentChip
                isLight
                key={attachment.key}
                attachment={attachment}
                onRemove={onRemoveAttachment}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div
          onMouseDown={(event) => event.preventDefault()}
          className="md:hidden"
        >
          <ComposeToolbar
            isCompact
            editor={editor}
            onLinkClick={() => setIsLinkModalOpen(true)}
          />
        </div>

        <Button
          variant={ButtonVariant.Primary}
          size={Size.Small}
          disabled={!hasContent || !canSend}
          isLoading={isSending}
          className="ml-auto"
          onClick={handleSend}
        >
          <Send className="size-4" />
          {t('global.send')}
        </Button>
      </div>

      <LinkModal
        editor={editor}
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
      />
    </div>
  );
};
